import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "../context/CartContext";

const navItems = [
    { to: "/shop", label: "Shop" },
    { to: "/our-story", label: "Our Story" },
    { to: "/our-story#tradition", label: "Tradition" },
    { to: "/process", label: "Process" },
    { to: "/process#ingredients", label: "Ingredients" },
    { to: "/contact", label: "Contact" },
];

const Header = () => {
    const { setOpen, totals } = useCart();
    const [mobile, setMobile] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`sticky top-0 z-40 transition-colors duration-300 ${
                scrolled ? "bg-[#FAF9F6]/95 backdrop-blur border-b border-[#E3DED5]" : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="logo-link"
                    className="font-serif text-2xl tracking-tight"
                >
                    ALATI
                </Link>

                <nav className="hidden md:flex items-center gap-10">
                    {navItems.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `text-[12px] uppercase tracking-[0.22em] font-medium transition-colors ${
                                    isActive ? "text-[#1C1C1A]" : "text-[#4A4A46] hover:text-[#1C1C1A]"
                                }`
                            }
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-4">
                    <button
                        data-testid="open-cart-button"
                        onClick={() => setOpen(true)}
                        className="relative flex items-center gap-2 text-[#1C1C1A] hover:text-[#4A4A46] transition-colors"
                        aria-label="Open cart"
                    >
                        <ShoppingBag size={20} strokeWidth={1.4} />
                        <span className="hidden sm:inline text-[11px] uppercase tracking-[0.22em]">
                            Cart
                        </span>
                        {totals.itemCount > 0 && (
                            <span
                                data-testid="cart-count"
                                className="absolute -top-2 -right-3 bg-[#1C1C1A] text-[#FAF9F6] text-[10px] w-5 h-5 rounded-full flex items-center justify-center"
                            >
                                {totals.itemCount}
                            </span>
                        )}
                    </button>
                    <button
                        className="md:hidden text-[#1C1C1A]"
                        onClick={() => setMobile(true)}
                        data-testid="open-mobile-menu"
                        aria-label="Open menu"
                    >
                        <Menu size={22} strokeWidth={1.4} />
                    </button>
                </div>
            </div>

            {/* Mobile menu */}
            {mobile && (
                <div
                    className="fixed inset-0 z-50 bg-[#FAF9F6] flex flex-col"
                    data-testid="mobile-menu"
                >
                    <div className="h-20 flex items-center justify-between px-6">
                        <span className="font-serif text-2xl">ALATI</span>
                        <button
                            onClick={() => setMobile(false)}
                            data-testid="close-mobile-menu"
                            aria-label="Close menu"
                        >
                            <X size={22} strokeWidth={1.4} />
                        </button>
                    </div>
                    <nav className="flex flex-col px-8 mt-8 gap-6">
                        {navItems.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                onClick={() => setMobile(false)}
                                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className="font-serif text-3xl text-[#1C1C1A]"
                            >
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>
                </div>
            )}
        </header>
    );
};

export default Header;
